import { DockingProvider } from "@asup/context-menu";
import { useAccount, useMsal } from "@azure/msal-react";
import { UnsavedChangesProvider } from "contexts/UnsavedChangesContextProvider";
import { AppSettingsLoaderData } from "functions/api/user/appSettingsLoader";
import { useReducer } from "react";
import { Outlet, useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { defaultSettings } from "./appsettings/defaultSettings";
import { unifiedReducer } from "./appsettings/unifiedReducer";
import { AppSettingsContext, unifiedInitialState } from "./AppSettingsContext";
import { KeyboardBindings } from "./KeyboardBindings";
import { SignalRProvider } from "./SignalRProvider";

/**
 * Provides unified application settings, hierarchy, TLF and ToC context to child components.
 * Initialises the shared undo history and keyboard bindings, and computes app-settings fields
 * directly from React Router loader data.
 */
export const AppSettingsContextProvider = (): React.ReactElement => {
  const loaderData = useLoaderData() as AppSettingsLoaderData;
  const navigate = useNavigate(); // Save not to use useCheckBeforeNavigate here - there are no unsaved changes in this context
  const location = useLocation();
  // MSAL info
  const { accounts } = useMsal();
  const account = useAccount(accounts[0]);
  const backEndUrl = loaderData.backEndUrl;
  const crystalUrl = loaderData.crystalUrl;

  let displayName = defaultSettings.displayName;
  let prid = defaultSettings.prid;
  /* istanbul ignore else */
  if (process.env.REACT_APP_STAGE === "local") {
    displayName = "TestUser";
    prid = "TestPrid";
  } else if (account) {
    displayName = `${account.name} (${account.username})`;
    prid = account.username.split("@")[0];
  }

  // Navigate for bad people
  if (
    account &&
    account.username.split("@")[1] !== defaultSettings.domain &&
    account.username.split("@")[0].length > 20 &&
    location.pathname !== "/NotAllowed"
  )
    navigate("/NotAllowed");

  // Get application permissions
  const userId =
    loaderData.me.userId ??
    // istanbul ignore next
    defaultSettings.userId;
  const isAdmin =
    loaderData.me.isAdmin ??
    // istanbul ignore next
    false;
  const applicationPermissions =
    loaderData.me.applicationPermissions ??
    // istanbul ignore next
    [];

  // Get back end version number
  const backEndVersion =
    loaderData.backEndVersion ??
    // istanbul ignore next
    "Error getting version";

  // Check maintenance mode
  const maintenanceMode = loaderData.maintenanceMode.maintenanceMode;
  const maintenanceMessage =
    loaderData.maintenanceMode.maintenanceMessage ??
    // istanbul ignore next
    "";

  // Redirect for maintenance mode
  /* istanbul ignore else */
  if (
    maintenanceMode &&
    !location.pathname.startsWith("/Admin") &&
    location.pathname !== "/Maintenance"
  )
    navigate("/Maintenance");

  // Extra context data
  const helpLinks = loaderData.helpLinks;
  const styles = loaderData.styles;
  const figureSettings = loaderData.figureSettings;
  const annotationComparatorTypes = loaderData.annotationComparatorTypes;
  const allowedReportTypes = loaderData.allowedReportTypes;

  // Unified reducer managing domain slices and their shared undo history
  const [state, dispatch] = useReducer(unifiedReducer, {
    ...unifiedInitialState,
    appSettingsLoaded: true,
    applicationPermissions,
    backEndUrl,
    backEndVersion,
    crystalUrl,
    displayName,
    figureSettings,
    helpLinks,
    isAdmin,
    maintenanceMessage,
    maintenanceMode,
    prid,
    styles,
    userId,
    annotationComparatorTypes,
    allowedReportTypes,
  });

  return (
    <AppSettingsContext.Provider value={{ state, dispatch }}>
      <KeyboardBindings />
      <SignalRProvider>
        <UnsavedChangesProvider>
          <DockingProvider
            minZIndex={900}
            maxZIndex={1049}
          >
            <div
              id="app-root"
              style={{ paddingBottom: "42px", height: "100vh", width: "100%" }}
            >
              <Outlet />
            </div>
          </DockingProvider>
        </UnsavedChangesProvider>
      </SignalRProvider>
    </AppSettingsContext.Provider>
  );
};

AppSettingsContextProvider.displayName = "AppSettingsContextProvider";

export default AppSettingsContextProvider;
